import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import type { UnifiedProductServiceResponse } from './types/UnifiedProductServiceResponse';

export interface PostGraphQLQueryArgs {
  endpoint: string;
  query: string;
  timeout: number;
  variables: Record<string, unknown>;
}

/**
 * Throws on every failure rather than degrading, because the two consumers need opposite policies:
 * the PDP soft-fails so the page still renders, while cmspages must be able to tell a failed
 * request from an empty result so its Redis cache does not store the failure. A client that
 * swallowed errors could not serve both.
 *
 * That includes a 200 carrying a GraphQL `errors` array, which is otherwise easy to read as an
 * empty success.
 */
export const postGraphQLQuery = async <TData>({
  endpoint,
  query,
  timeout,
  variables,
}: PostGraphQLQueryArgs): Promise<TData> => {
  // fetchJSONWithTimeout already sets Content-Type: application/json on every request.
  const response = await fetchJSONWithTimeout(endpoint, {
    body: { query, variables },
    method: 'POST',
    timeout,
  });

  if (!response.ok) {
    throw new Error(`Unified product service responded ${response.status} ${response.statusText}`);
  }

  const body = (await response.json()) as UnifiedProductServiceResponse<TData>;

  if (body.errors?.length) {
    throw new Error(
      `Unified product service returned errors: ${body.errors
        .map(({ message }) => message)
        .join('; ')}`,
    );
  }

  if (body.data == null) {
    throw new Error('Unified product service returned no data');
  }

  return body.data;
};
